import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { downloadFile } from '../utils/fileUtils';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPaymentPointOrganizers } from '../redux/slices/paymentPointsSlice';
import { fetchOrganizerPaymentPoints, fetchOrganizers } from '../redux/slices/organizersSlice';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { TIMEOUT_NONE } from '../constants';
import api from '../api';

export default function SaleReportsDialog({ open, onClose }) {
  const { t } = useTranslation();

  const [selectedOrganizer, setSelectedOrganizer] = useState('');
  const [selectedCashier, setSelectedCashier] = useState('-1');
  const [onlineChannel, setOnlineChannel] = useState(true);
  const [cashierChannel, setCashierChannel] = useState(true);
  const [organizerChannel, setOrganizerChannel] = useState(true);
  const [freeTickets, setFreeTickets] = useState(true);
  const [showCommissionInReports, setShowCommissionInReports] = useState(true);
  const [showCanceledOrdersInReports, setShowCanceledOrdersInReports] = useState(true);
  const [paidTickets, setPaidTickets] = useState(true);
  const [selectedType, setSelectedType] = useState('');
  const [ordersFrom, setOrdersFrom] = useState(null);
  const [ordersTo, setOrdersTo] = useState(null);
  const [eventsFrom, setEventsFrom] = useState(null);
  const [eventsTo, setEventsTo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();
  const organizers = useSelector((state) => state.organizers.data);
  const paymentPointOrganizers = useSelector((state) => state.paymentPoints.paymentPointOrganizers);
  const organizerPaymentPoints = useSelector((state) => state.organizers.organizerPaymentPoints);

  const user = useSelector((state) => state.auth.user);

  const downloadDisabled = useMemo(() => {
    if (selectedType === 'canceledTickets') {
      return !selectedType || (user.role === 'cashier' && !selectedOrganizer) || !ordersFrom || !ordersTo;
    } else if (user.role === 'cashier' && selectedType === 'cashRegister') {
      return !selectedType || !selectedOrganizer || !ordersFrom || !ordersTo;
    } else if (user.role === 'cashier' && selectedType !== 'cashRegister') {
      return (
        !selectedType ||
        !selectedOrganizer ||
        !ordersFrom ||
        !ordersTo ||
        !eventsFrom ||
        !eventsTo ||
        [freeTickets, paidTickets].every((v) => !v)
      );
    } else if (user.role === 'organizer') {
      return (
        !selectedType ||
        !ordersFrom ||
        !ordersTo ||
        !eventsFrom ||
        !eventsTo ||
        [onlineChannel, cashierChannel, organizerChannel].every((v) => !v) ||
        [freeTickets, paidTickets].every((v) => !v)
      );
    } else if (user.role === 'admin') {
      return (
        !selectedOrganizer ||
        !selectedType ||
        !ordersFrom ||
        !ordersTo ||
        !eventsFrom ||
        !eventsTo ||
        [onlineChannel, cashierChannel, organizerChannel].every((v) => !v) ||
        [freeTickets, paidTickets].every((v) => !v)
      );
    } else {
      return true;
    }
  }, [
    user.role,
    selectedType,
    selectedOrganizer,
    ordersFrom,
    ordersTo,
    eventsFrom,
    eventsTo,
    onlineChannel,
    cashierChannel,
    organizerChannel,
    freeTickets,
    paidTickets,
  ]);

  const onCloseInternal = (event, reason) => {
    if (reason !== 'backdropClick') {
      setFreeTickets(true);
      setPaidTickets(true);
      setShowCommissionInReports(true);
      setShowCanceledOrdersInReports(true);
      user.role === 'cashier' ? setOnlineChannel(false) : setOnlineChannel(true);
      setCashierChannel(true);
      user.role === 'cashier' ? setOrganizerChannel(false) : setOrganizerChannel(true);
      setSelectedOrganizer('');
      user.role === 'cashier' ? setSelectedCashier(user.payment_point_id) : setSelectedCashier('-1');
      user.role === 'cashier' ? setSelectedType('cashRegister') : setSelectedType('');
      setOrdersFrom(null);
      setOrdersTo(null);
      setEventsFrom(null);
      setEventsTo(null);
      onClose();
    }
  };

  const onConfirmInternal = () => {
    setIsLoading(true);
    api
      .get(
        `/reports?${freeTickets ? `free=true` : ''}${paidTickets ? `&paid=true` : ''}${
          showCommissionInReports ? `&show_commission_in_reports=true` : ''
        }${showCanceledOrdersInReports ? `&show_canceled_orders_in_reports=true` : ''}${
          onlineChannel ? '&online_channel=true' : ''
        }${cashierChannel ? '&cashier_channel=true' : ''}${
          organizerChannel ? '&organizer_channel=true' : ''
        }&type=${encodeURIComponent(selectedType)}&orders_from=${encodeURIComponent(
          ordersFrom.toISOString()
        )}&orders_to=${encodeURIComponent(ordersTo.toISOString())}${
          eventsFrom ? `&events_from=${encodeURIComponent(eventsFrom.toISOString())}` : ''
        }${
          eventsTo ? `&events_to=${encodeURIComponent(eventsTo.toISOString())}` : ''
        }&organizer_id=${encodeURIComponent(selectedOrganizer)}&payment_point_id=${encodeURIComponent(
          selectedCashier
        )}`,
        { timeout: TIMEOUT_NONE, responseType: 'blob' }
      )
      .then((res) => {
        const data = res.data;
        setIsLoading(false);
        downloadFile(data, 'application/pdf', `${selectedType.toLowerCase()}_report_${dayjs().unix()}.pdf`);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  useEffect(() => {
    if (user.role === 'cashier') {
      setSelectedType('cashRegister');
      setCashierChannel(true);
      setOnlineChannel(false);
      setOrganizerChannel(false);
      setSelectedCashier(user.payment_point_id);
    }
  }, [user.payment_point_id, user.role]);

  useEffect(() => {
    if (user) {
      if (user.role === 'cashier') {
        dispatch(fetchPaymentPointOrganizers({ paymentPointId: user.payment_point_id }));
      }
      if (user.role === 'organizer') {
        dispatch(fetchOrganizerPaymentPoints({ organizerId: user.organizer_id }));
      }
      if (user.role === 'admin') {
        dispatch(fetchOrganizers());
        if (selectedOrganizer) {
          dispatch(fetchOrganizerPaymentPoints({ organizerId: selectedOrganizer }));
        }
      }
    }
  }, [dispatch, user, user.role, selectedOrganizer]);

  const reportTypes = useMemo(() => {
    if (user.role === 'cashier') {
      return [
        {
          id: 'cashRegister',
          value: t('Cash Register'),
        },
        {
          id: 'detailedSales',
          value: t('Detailed Sales'),
        },
        {
          id: 'sales',
          value: t('Sales'),
        },
        { id: 'salesByPrice', value: t('Sales By Price') },
      ];
    } else if (user.role === 'organizer' || user.role === 'admin') {
      return [
        {
          id: 'detailedSales',
          value: t('Detailed Sales'),
        },
        {
          id: 'sales',
          value: t('Sales'),
        },
        { id: 'salesByPrice', value: t('Sales By Price') },
        { id: 'canceledTickets', value: t('Canceled Tickets') },
      ];
    }
  }, [user.role, t]);

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onCloseInternal}>
      <DialogTitle gutterBottom>
        <Box display="flex" alignItems="center">
          <AssessmentIcon sx={{ marginRight: 1 }} />
          {t('Sale Reports')}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 12,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {(user.role === 'cashier' || user.role === 'admin') && (
          <TextField
            sx={{ marginBottom: 2 }}
            value={selectedOrganizer}
            onChange={(event) => setSelectedOrganizer(event.target.value)}
            fullWidth
            select
            label={t('Organizer')}
          >
            {(user.role === 'cashier' ? paymentPointOrganizers : organizers).map((org) => (
              <MenuItem key={org.id} value={org.id}>
                {org.name}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          sx={{ marginBottom: 2 }}
          value={selectedType}
          onChange={(event) => setSelectedType(event.target.value)}
          fullWidth
          select
          label={t('Type')}
        >
          {reportTypes.map(({ id, value }) => (
            <MenuItem key={id} value={id}>
              {value}
            </MenuItem>
          ))}
        </TextField>
        {(user.role === 'organizer' || user.role === 'admin') && selectedType !== 'canceledTickets' && (
          <>
            <Divider>{t('Channels')}</Divider>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox checked={onlineChannel} onChange={(event) => setOnlineChannel(event.target.checked)} />
                }
                label={t('Online')}
              />
              <FormControlLabel
                sx={{ marginBottom: 1 }}
                control={
                  <Checkbox checked={cashierChannel} onChange={(event) => setCashierChannel(event.target.checked)} />
                }
                label={t('Cashier')}
              />
              {cashierChannel && (
                <TextField
                  value={selectedCashier}
                  onChange={(event) => setSelectedCashier(event.target.value)}
                  fullWidth
                  select
                  label={t('Cashier')}
                >
                  <MenuItem key={'-1'} value={'-1'}>
                    {t('All')}
                  </MenuItem>
                  {organizerPaymentPoints.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={organizerChannel}
                    onChange={(event) => setOrganizerChannel(event.target.checked)}
                  />
                }
                label={t('Organizer')}
              />
            </FormGroup>
          </>
        )}
        {selectedType !== 'cashRegister' && selectedType !== 'canceledTickets' && (
          <>
            <Divider sx={{ marginBottom: 2 }}>{t('Events')}</Divider>
            <DateTimePicker
              sx={{ marginBottom: 2 }}
              value={eventsFrom}
              onChange={(value) => setEventsFrom(value)}
              maxDateTime={eventsTo}
              slotProps={{ textField: { label: t('From'), fullWidth: true, variant: 'outlined' } }}
            />
            <DateTimePicker
              sx={{ marginBottom: 2 }}
              value={eventsTo}
              onChange={(value) => setEventsTo(value)}
              minDateTime={eventsFrom}
              slotProps={{ textField: { label: t('To'), fullWidth: true, variant: 'outlined' } }}
            />
          </>
        )}

        <Divider sx={{ marginBottom: 2 }}>{t('Orders')}</Divider>
        <DateTimePicker
          sx={{ marginBottom: 2 }}
          value={ordersFrom}
          onChange={(value) => setOrdersFrom(value)}
          disableFuture
          slotProps={{ textField: { label: t('From'), fullWidth: true, variant: 'outlined' } }}
        />
        <DateTimePicker
          sx={{ marginBottom: 2 }}
          value={ordersTo}
          onChange={(value) => setOrdersTo(value)}
          disableFuture
          slotProps={{ textField: { label: t('To'), fullWidth: true, variant: 'outlined' } }}
        />

        {selectedType !== 'cashRegister' && selectedType !== 'canceledTickets' && (
          <>
            <Divider>{t('Prices')}</Divider>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox checked={paidTickets} onChange={(event) => setPaidTickets(event.target.checked)} />}
                label={t('Paid')}
              />
              <FormControlLabel
                control={<Checkbox checked={freeTickets} onChange={(event) => setFreeTickets(event.target.checked)} />}
                label={t('Free')}
              />
              {
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showCommissionInReports}
                      onChange={(event) => setShowCommissionInReports(event.target.checked)}
                    />
                  }
                  label={t('showCommissionInReports')}
                />
              }
            </FormGroup>
          </>
        )}
        {selectedType !== 'canceledTickets' && (
          <FormControlLabel
            control={
              <Checkbox
                checked={showCanceledOrdersInReports}
                onChange={(event) => setShowCanceledOrdersInReports(event.target.checked)}
              />
            }
            label={t('showCanceledOrdersInReports')}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={isLoading || downloadDisabled} onClick={onConfirmInternal} variant="contained">
          {t('Download')}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
